import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Grid, Divider } from '@material-ui/core';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import sankey from 'highcharts/modules/sankey';
import { Overlay } from 'src/common-ui';
import Subheader from 'src/components/Subheader/Subheader.container';
import SubheaderDropdown from 'src/components/Subheader/SubheaderDropdown';
import { ViewDataState } from 'src/types/Domain';
import { TenantConfigViewItem } from 'src/dao/tenantConfigClient';
import { SankeyGroupedViewDefn, SankeySideChart } from 'configuration-schemas/viewdefns/viewdefn';
import { SankeySelectionOption, SankeySelectionsBlock, SankeyChartBlock, SankeyViewBlock } from './SankeyGroupedView.types';
import { buildPieSeries, buildHighchartsSankey, selectSankeyGroupPath } from './SankeyGroupedView.selectors';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';


sankey(Highcharts);

export interface SankeyGroupedViewStateProps {
  sankeyDefn?: SankeyGroupedViewDefn;
  sankeyData: BasicPivotItem[];
  sankeyDataState: ViewDataState;

  detailData: BasicPivotItem[];
  detailDataState: ViewDataState;

  selectedMemberId?: string;

  // Optional props
  groupByDefnId?: string;
  groupByKey?: string;
  flowStatusKey?: string;
  title?: string;
}

export interface SankeyGroupedViewDispatchProps {
  onShowView: () => void;
  onRefresh: () => void;
  onElementClick: (memberId?: string) => void;
  onDestroy: () => void;
}

export type SankeyGroupedViewProps = SankeyGroupedViewStateProps & SankeyGroupedViewDispatchProps;

const nodeEmptyMessage = (
  <div style={{ padding: '12px 0', color: '#888' }} data-qa="sankey-node-empty">
    Select a node in the Sankey to view details.
  </div>
);

const sankeyEmptyMessage = (
  <div style={{ padding: '12px 0', color: '#888' }} data-qa="sankey-empty">
    No Sankey data to display.
  </div>
);

const sideEmptyMessage = (
  <div style={{ padding: '12px 0', color: '#888' }} data-qa="side-empty">
    No detail data to display.
  </div>
);

export const SankeyGroupedView = (props: SankeyGroupedViewProps) => {
  const {
    onShowView,
    onRefresh,
    onElementClick,
    onDestroy,

    sankeyDefn,
    sankeyData,
    sankeyDataState,

    detailData,
    detailDataState,

    selectedMemberId,

    groupByDefnId,
    groupByKey,
    flowStatusKey,

    title,
  } = props;

  const [groupingIdx, setGroupingIdx] = useState(0);

  const viewBlocks: SankeyViewBlock[] = useMemo(
    () => (Array.isArray(sankeyDefn?.view) ? (sankeyDefn?.view as SankeyViewBlock[]) : []),
    [sankeyDefn],
  );

  const selectionBlock = useMemo(
    () => viewBlocks.find((b): b is SankeySelectionsBlock => (b as SankeySelectionsBlock).xtype === 'sankey-chart'),
    [viewBlocks],
  );

  const groupingOptionsFromView = useMemo(() => {
    const opts = selectionBlock?.view || [];
    return opts.map((opt: SankeySelectionOption) => {
      const path: string[] = (opt.view ?? []).map((v) => v.dataIndex).filter(Boolean);
      return {
        text: opt.text || path.join(' / '),
        dataIndex: path.join(','),
        value: path,
      };
    });
  }, [selectionBlock]);

  const groupingOptions = useMemo(() => {
    return groupingOptionsFromView.map((g: any) => {
      const path = g.value;
      const text = g.text || (Array.isArray(path) ? path.join(' / ') : String(path));
      const dataIndex = Array.isArray(path) ? path.join(',') : String(path);
      return { text, dataIndex, value: path };
    });
  }, [groupingOptionsFromView]);

  const fallbackGroupingFromView = useMemo(() => {
    const path = selectionBlock?.defaultPath || groupingOptionsFromView[0]?.value;
    return Array.isArray(path) ? path : path ? [path] : [];
  }, [selectionBlock, groupingOptionsFromView]);

  useEffect(() => {
    onShowView();
    return onDestroy;
  }, [onShowView, onDestroy]);

  useEffect(() => {
    if (sankeyDefn) {
      onRefresh();
    }
  }, [sankeyDefn, groupByKey, flowStatusKey, onRefresh]);


  const grouping = useMemo(() => {
    const baseDef = selectSankeyGroupPath(sankeyDefn);
    const base = baseDef.length ? baseDef : fallbackGroupingFromView;
    const rawSelected = groupingOptions[groupingIdx]?.value;

    const normalizeSelected = (opt: any): string[] => {
      if (Array.isArray(opt)) return opt;
      if (typeof opt === 'string') return opt.split(',').map((s) => s.trim()).filter(Boolean);
      return [];
    };

    const selected = normalizeSelected(rawSelected);
    if (selected.length) return selected;

    return base;
  }, [sankeyDefn, groupingOptions, groupingIdx, fallbackGroupingFromView]);

  const { nodes, data } = useMemo(() => {
    if (!sankeyDefn) return { nodes: [], links: [], data: [] };
    return buildHighchartsSankey(sankeyData, sankeyDefn.metricIndex, grouping, 'All products');
  }, [sankeyData, sankeyDefn, grouping]);

  const chartBlock = useCallback(
    (xtype: 'left-chart' | 'right-chart') =>
      viewBlocks.find((b): b is SankeyChartBlock => (b as SankeyChartBlock).xtype === xtype),
    [viewBlocks],
  );

  const leftChartsFromView = useMemo(() => {
    const block = chartBlock('left-chart');
    return (block?.view || []).map((v, idx) => ({
      text: v.text || `Metric ${idx + 1}`,
      dataIndex: v.dataIndex,
      renderer: v.renderer,
    }));
  }, [chartBlock]);

  const rightChartsFromView = useMemo(() => {
    const block = chartBlock('right-chart');
    return (block?.view || []).map((v, idx) => ({
      text: v.text || `Metric ${idx + 1}`,
      dataIndex: v.dataIndex,
      renderer: v.renderer,
    }));
  }, [chartBlock]);

  const sideChartList: SankeySideChart[] = useMemo(() => {
    const combined = [...leftChartsFromView, ...rightChartsFromView];
    return combined.length ? combined : [];
  }, [leftChartsFromView, rightChartsFromView]);

  useEffect(() => {
    // reset when config changes
    setGroupingIdx(0);
  }, [sankeyDefn]);

  const handleGroupingDropdown = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setGroupingIdx(Number(event.target.value));
  };

  const sankeyOptions = useMemo(() => {
    const safeTitle = sankeyDefn?.title || 'Allocation Flow';

    return {
      chart: {
        animation: false,
        panning: { enabled: false },
      },
      title: { text: '' },
      accessibility: { enabled: false },
      tooltip: {
        pointFormat: '<b>{point.name}</b>: {point.weight:.0f}',
      },
      plotOptions: {
        series: {
          clip: false,
          animation: false,
        },
      },
      series: [
        {
          type: 'sankey',
          name: safeTitle,
          keys: ['from', 'to', 'weight'],
          data,
          nodes,
          minLinkWidth: 6,
          linkOpacity: 0.7,
          borderWidth: 0,
          nodePadding: 24,
          curveFactor: 0.5,
          dataLabels: {
            enabled: true,
            style: { fontSize: '11px' },
          },
          point: {
            events: {
              click: function () {

                // Only nodes will have the memberId we attached via `nodes: [...]`.
                // @ts-ignore Highcharts typing doesn't know our custom `memberId` prop, but we put it there in `buildHighchartsSankey`.
                const isNode = !!this.isNode;
                if (!isNode) return;


                const memberId: string | undefined = this.options?.custom?.memberId;
                if (memberId) onElementClick(memberId);
              },
            },
          },
        },
      ],
      credits: { enabled: false },
    } as Highcharts.Options;
  }, [data, nodes, onElementClick, sankeyDefn?.title]);

  const renderPie = (chart: SankeySideChart, key: number) => {
    const hasData = detailDataState === ViewDataState.liveDataReady && detailData && detailData.length > 0;
    const series = hasData ? [buildPieSeries(detailData, chart)] : [];

    return (
      <div key={key} style={{ width: '100%', minHeight: 240, padding: '0 8px' }}>
        <Overlay visible={detailDataState === ViewDataState.liveDataLoadingNoCache} type="loading" fitParent />
        {hasData ? (
          <HighchartsReact
            highcharts={Highcharts}
            options={{
              chart: {
                type: 'pie',
                height: 240,
                spacing: [30, 0, 4, 0],
              },
              title: { text: chart.text || '', margin: 10, y: 10 },
              series,
              tooltip: {
                pointFormat: '<b>{point.y:.0f}</b> ({point.percentage:.1f}%)',
              },
              plotOptions: {
                pie: {
                  allowPointSelect: true,
                  size: '80%',
                  center: ['50%', '52%'],
                  dataLabels: {
                    enabled: true,
                    format: '<b>{point.name}</b>: {point.percentage:.1f} %',
                    distance: 12,
                  },
                },
              },
              credits: { enabled: false },
            }}
          />
        ) : (
          sideEmptyMessage
        )}
      </div>
    );
  };

  const hasSankeyData = !!sankeyDefn && data.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }} data-qa="sankey-grouped-view">
      <Subheader
        title={title}
        groupByDefn={groupByDefnId}
        pareDownDefn={undefined}
        sortByDefn={undefined}
        hideTitle={!title}
        showSearch={false}
        showFlowStatus={false}
        viewDataState={[sankeyDataState]}
      />

      <div style={{ flex: 1, padding: '12px', overflow: 'hidden' }}>
        <Grid container spacing={2} style={{ height: '100%' }}>
          <Grid item xs={12} md={8} style={{ height: '100%' }}>
            {groupingOptions.length > 1 ? (
              <div style={{ marginBottom: 8 }}>
                <SubheaderDropdown
                  defaultSelection={0}
                  selection={groupingIdx}
                  options={groupingOptions as unknown as TenantConfigViewItem[]}
                  label="Select Level"
                  handleChangeOnDropdown={handleGroupingDropdown}
                />
              </div>
            ) : null}
            <div
              style={{
                height: '100%',
                minHeight: 360,
                border: '1px solid #e0e0e0',
                borderRadius: 8,
                position: 'relative',
              }}
            >
              <Overlay visible={sankeyDataState === ViewDataState.liveDataLoadingNoCache} type="loading" fitParent />

              {hasSankeyData ? (
                <HighchartsReact
                  highcharts={Highcharts}
                  options={sankeyOptions}
                  containerProps={{ style: { height: '100%' } }}
                />
              ) : sankeyDataState === ViewDataState.liveDataReady ? (
                sankeyEmptyMessage
              ) : (
                // before first successful load
                sankeyEmptyMessage
              )}
            </div>
          </Grid>

          <Grid item xs={12} md={4} style={{ height: '100%' }}>
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly' }}>
              <Divider style={{ marginBottom: 8 }} />
              {sideChartList && sideChartList.length
                ? sideChartList.map((chart, idx) => renderPie(chart, idx))
                : nodeEmptyMessage}
            </div>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default SankeyGroupedView;
